import React from "react";
import PageHeader from "../components/PageHeader";

const Milestone = ({ year, events }) => (
  <div className="flex flex-col md:flex-row gap-8 md:gap-24 border-b border-white/10 py-16 last:border-0">
    {/* Year Column */}
    <div className="md:w-1/4 flex-shrink-0">
      <span className="text-6xl md:text-8xl font-black text-dreibach-teal tracking-tighter leading-none opacity-90">
        {year}
      </span>
    </div>

    {/* Content Column */}
    <div className="md:w-3/4 space-y-12">
      {events.map((event, idx) => (
        <div key={idx} className="relative pl-8 border-l border-white/20">
          <div className="absolute -left-1.5 top-2 w-3 h-3 bg-dreibach-teal rounded-full"></div>
          {event.title && (
            <h3 className="text-2xl font-bold text-white uppercase tracking-wide mb-3">
              {event.title}
            </h3>
          )}
          <div className="text-gray-400 text-lg leading-relaxed max-w-2xl">
            {event.description}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const About = () => {
  const history = [
    {
      year: "2018",
      events: [
        {
          title: "The Beginning",
          description: "Founded as a B2B sourcing business, specialising in importing products from the Far East for e-commerce brands."
        }
      ]
    },
    {
      year: "2019",
      events: [
        {
          title: "First In-House Brand",
          description: "Launched our first in-house brand, Tanness, marking our move into building and owning our own product ranges."
        }
      ]
    },
    {
      year: "2020",
      events: [
        {
          title: "Ownership Transition",
          description: "Babandeep acquired 100% ownership of the company."
        },
        {
          title: "First Warehouse",
          description: "Opened our first 8,000 sq ft warehouse in Borehamwood, bringing logistics and fulfilment in-house."
        }
      ]
    },
    {
      year: "2021",
      events: [
        {
          title: "China Sourcing Office",
          description: "Established our China sourcing office in Shenzhen to strengthen supplier relationships and quality control."
        },
        {
          title: "D2C Market Entry",
          description: "Entered the Direct-to-Consumer (D2C) market."
        },
        {
          title: "New Brand Launches",
          description: "Launched two new brands: Pelle & Sol and Fur & Bone."
        }
      ]
    },
    {
      year: "2023",
      events: [
        {
          title: "New Headquarters",
          description: "Moved into our new two-storey headquarters, dedicated entirely to our growing team and operations."
        },
        {
          title: "Yum Yums Acquisition",
          description: "In March 2023, acquired 100% of Yum Yums, the UK’s best-selling weaning set on Amazon."
        }
      ]
    },
    {
      year: "2024",
      events: [
        {
          title: "SA Products Acquisition",
          description: "In November, acquired the brand and assets of SA Products in a strategic deal to protect jobs and preserve a well-loved brand."
        }
      ]
    },
    {
      year: "2025",
      events: [
        {
          title: "Northampton DC 1",
          description: "In May, opened a 45,000 sq ft distribution centre in Northampton to support our expanding stockholding and D2C growth."
        },
        {
          title: "Northampton DC 2",
          description: "In November, opened a second 45,000 sq ft Northampton facility on the same industrial estate, giving us 8,000 pallet spaces across our network."
        },
        {
          title: "D2C Expansion",
          description: "Expanded our D2C reach by launching on B&Q, Debenhams, TikTok, Temu and OnBuy, processing over 10,000 orders per day from our distribution centres."
        }
      ]
    },
    {
      year: "2026",
      events: [
        {
          title: "Spring Fair Debut",
          description: "Exhibited at Spring Fair 2026 for the first time, marking a major step forward in our retail and brand visibility journey."
        }
      ]
    }
  ];

  return (
    <div className="bg-dreibach-black min-h-screen pb-24">
      <PageHeader 
        title="Our Story" 
        subtitle="From a B2B sourcing specialist to a powerhouse House of Brands."
        image="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=2000"
      />

      {/* Portfolio Overview Text */}
      <div className="bg-dreibach-black py-20 border-b border-white/10">
         <div className="container mx-auto px-6 max-w-4xl text-center">
            <h2 className="heading-md text-white mb-8">A Brand Portfolio Built for Modern Retail</h2>
            <p className="text-gray-400 text-lg leading-relaxed mb-6">
               Dreibach’s brand portfolio is built for modern retail: practical, margin-friendly products with clear positioning, retail-ready packaging, and consistent SKU management across multiple categories.
            </p>
            <p className="text-gray-400 text-lg leading-relaxed">
               Each brand is designed to sit confidently on shelf and online, with strong product photography, clean benefit-led messaging, and a focus on the features customers actually search for and repurchase.
            </p>
         </div>
      </div>

      {/* CEO Quote Section */}
      <div className="bg-dreibach-card py-24 border-y border-white/10">
         <div className="container mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">
            <div className="order-2 md:order-1">
               <div className="relative">
                  <div className="absolute top-0 left-0 text-dreibach-teal text-9xl font-serif opacity-20 -translate-y-12">"</div>
                  <blockquote className="relative z-10 text-xl md:text-2xl text-white font-light italic leading-relaxed mb-8">
                     We are driven by a mission to create exceptional value. By combining affordability with uncompromising quality, we empower retailers to deliver products that stand out. We do everything in-house, eliminating intermediaries.
                  </blockquote>
                  <div>
                     <p className="text-white font-bold uppercase tracking-wide">Babandeep Oberoi</p>
                     <p className="text-dreibach-teal text-sm uppercase tracking-widest font-bold">CEO, Dreibach Group</p>
                  </div>
               </div>
            </div>
            <div className="order-1 md:order-2 flex justify-center">
               <div className="relative w-72 h-72 md:w-96 md:h-96">
                  <div className="absolute inset-0 border-2 border-dreibach-teal translate-x-4 translate-y-4"></div>
                  <img 
                     src="https://customer-assets.emergentagent.com/job_dreibach-site/artifacts/tycy40op_mike_put_some_window_lightin_in_the_background_and_make_it_a_bit_mroe_25ee90cb-dae7-42cf-8452-d08133b6e0cc.png" 
                     alt="Babandeep Oberoi" 
                     className="w-full h-full object-cover grayscale relative z-10"
                  />
               </div>
            </div>
         </div>
      </div>

      {/* The Timeline */}
      <div className="bg-dreibach-black py-24">
         <div className="container mx-auto px-6">
            <h2 className="heading-lg text-white mb-20 text-center border-b border-white/10 pb-8">Our Milestones</h2>
            <div className="max-w-5xl mx-auto">
               {history.map((item, idx) => (
                 <Milestone key={idx} year={item.year} events={item.events} />
               ))}
            </div>
         </div>
      </div>

      {/* Partners Section */}
      <div className="bg-dreibach-card py-24 border-t border-white/10">
         <div className="container mx-auto px-6">
            <h3 className="heading-md text-white mb-16 text-center uppercase tracking-widest">Companies We Work With</h3>
            <div className="flex flex-wrap justify-center gap-12 md:gap-20 items-center">
               {[
                 { name: "Temu", url: "https://customer-assets.emergentagent.com/job_dreibach-site/artifacts/qiu6dsdq_Temu_logo.svg.png" },
                 { name: "Amazon", url: "https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg" },
                 { name: "B&Q", url: "https://customer-assets.emergentagent.com/job_dreibach-site/artifacts/f31pbfih_BQ-1024x440.png" },
                 { name: "Debenhams", url: "https://customer-assets.emergentagent.com/job_dreibach-site/artifacts/gxu9y27g_DEBENHAMS_LOGO_2023_%281%29.png" },
                 { name: "Robert Dyas", url: "https://customer-assets.emergentagent.com/job_dreibach-site/artifacts/9ye525gd_robert-dyas.svg" },
                 { name: "OnBuy", url: "https://customer-assets.emergentagent.com/job_dreibach-site/artifacts/6tt2ax19_logo-onbuy-1668099884.webp", scale: 1.5 },
                 { name: "TJC", url: "https://customer-assets.emergentagent.com/job_dreibach-site/artifacts/hylrtk3g_TJC_New_Logo.png" },
                 { name: "TikTok", url: "https://upload.wikimedia.org/wikipedia/en/a/a9/TikTok_logo.svg" },
                 { name: "eBay", url: "https://upload.wikimedia.org/wikipedia/commons/1/1b/EBay_logo.svg" }
               ].map((partner, i) => (
                  <div key={i} className="h-20 w-40 flex items-center justify-center bg-transparent">
                     <img 
                       src={partner.url} 
                       alt={partner.name} 
                       style={partner.scale ? { transform: `scale(${partner.scale})` } : {}}
                       className="max-h-full max-w-full object-contain brightness-0 invert opacity-90 hover:opacity-100 transition-all duration-300"
                     />
                  </div>
               ))}
            </div>
         </div>
      </div>
    </div>
  );
};

export default About;
